import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AdminIncidentFilters,
  CloseIncidentData,
  CreateIncidentData,
  IncidentEventVisibility,
  UpdateIncidentData,
  incidentsApi,
} from '@/services';

export const incidentsKeys = {
  all: ['incidents'] as const,
  mine: ['incidents', 'mine'] as const,
  detail: (id: string) => ['incidents', 'detail', id] as const,
  unread: ['incidents', 'unread'] as const,
  adminList: (filters: AdminIncidentFilters) => ['incidents', 'admin', filters] as const,
};

export function useMyIncidentsQuery(enabled = true) {
  return useQuery({
    queryKey: incidentsKeys.mine,
    queryFn: () => incidentsApi.listMine(),
    enabled,
  });
}

export function useAdminIncidentsListQuery(filters: AdminIncidentFilters, enabled = true) {
  return useQuery({
    queryKey: incidentsKeys.adminList(filters),
    queryFn: () => incidentsApi.adminList(filters),
    enabled,
  });
}

export function useIncidentQuery(id: string | undefined) {
  return useQuery({
    queryKey: incidentsKeys.detail(id ?? ''),
    queryFn: () => incidentsApi.get(id!),
    enabled: !!id,
  });
}

export function useIncidentUnreadCountQuery(enabled = true) {
  return useQuery({
    queryKey: incidentsKeys.unread,
    queryFn: () => incidentsApi.unreadCount(),
    enabled,
  });
}

export function useReportIncidentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIncidentData) => incidentsApi.report(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useUpdateIncidentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIncidentData }) => incidentsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function usePostIncidentMessageMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, visibility }: { id: string; body: string; visibility?: IncidentEventVisibility }) =>
      incidentsApi.postMessage(id, body, visibility),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useMarkIncidentReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => incidentsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useSetIncidentStudentsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, students }: { id: string; students: CreateIncidentData['students'] }) =>
      incidentsApi.setStudents(id, students),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useSetIncidentStaffMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, staffIds }: { id: string; staffIds: string[] }) => incidentsApi.setStaff(id, staffIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useChangeIncidentStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => incidentsApi.changeStatus(id, 'investigating'),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useCloseIncidentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CloseIncidentData }) => incidentsApi.close(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useUploadIncidentAttachmentsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, files }: { incidentId: string; files: File[] }) =>
      incidentsApi.uploadAttachments(incidentId, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}

export function useDeleteIncidentAttachmentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => incidentsApi.deleteAttachment(attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: incidentsKeys.all }),
  });
}
